// AF-3.16 — ExperimentCard: Experiment Command Center's core unit.
//
// CRITICAL COPY RULE (spec Section 9): this component's own static
// labels must never say "winner", "winning", "customers prefer",
// "proven to increase", "scale this", or "guaranteed improvement" --
// and it must never rewrite the backend's own result/repeat_decision
// into any of those. All outcome-describing text is either the
// backend's own field value or one of the fixed, reviewed strings below.

import { StatusBadge } from "./Badges";

const REPEAT_DECISION_COPY = {
  REPEAT_JUSTIFIED: "The prior result was inconclusive for a reason that is addressable -- a repeat may be justified.",
  DO_NOT_REPEAT: "A conclusive result already exists. Repeating would not add new evidence.",
  PRIORITIZE_GUARDRAIL_FIRST: "Address the guardrail breach before repeating the primary intervention.",
  PRIORITIZE_MEASUREMENT_FIRST: "Establish sufficient measurement before deciding whether to repeat.",
  CONTINUE_RUNNING: "This experiment is currently running or being measured.",
  NOT_APPLICABLE: "No prior evidence exists for this experiment.",
  INSUFFICIENT_EVIDENCE: "Not enough evidence exists to decide whether a repeat is justified.",
};

export function ExperimentCard({ experiment, testId = "experiment-card" }) {
  if (!experiment) return null;
  const {
    status, outcome_status, result, guardrail_breaches = [], primary_metric,
    guardrails = [], what_was_tested, what_was_observed, what_remains_unknown,
    repeat_decision, hypothesis,
  } = experiment;

  return (
    <div data-testid={testId} className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-0.5">Hypothesis</p>
          <p className="text-[13px] text-[var(--text-primary)]">{hypothesis || what_was_tested || "Not provided by backend"}</p>
        </div>
        {(outcome_status || result) && <StatusBadge value={result || outcome_status} />}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3 text-[11px]">
        <div>
          <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Primary metric</p>
          <p className="text-[var(--text-primary)]">{primary_metric || "—"}</p>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Guardrails</p>
          <p className="text-[var(--text-primary)]">{guardrails.length ? guardrails.join(", ") : "—"}</p>
        </div>
      </div>

      {guardrail_breaches.length > 0 && (
        <div className="mb-3 px-2.5 py-2 rounded bg-[var(--error-bg)] border border-[var(--error-border)]">
          <p className="text-[10px] font-mono uppercase text-[var(--error)]">Guardrail breached</p>
          <p className="text-[11px] text-[var(--text-secondary)]">{guardrail_breaches.join(", ")}</p>
        </div>
      )}

      {what_was_observed && (
        <div className="mb-2">
          <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">What happened</p>
          <p className="text-[11px] text-[var(--text-secondary)]">{what_was_observed}</p>
        </div>
      )}

      {what_remains_unknown && (
        <div className="mb-2">
          <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">What remains unknown</p>
          <p className="text-[11px] text-[var(--text-secondary)]">{what_remains_unknown}</p>
        </div>
      )}

      {repeat_decision && (
        <div className="mt-3 pt-3 border-t border-[var(--border)]">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Recommended</p>
            <StatusBadge value={repeat_decision} />
          </div>
          <p className="text-[11px] text-[var(--text-secondary)]">{REPEAT_DECISION_COPY[repeat_decision] || "See evidence for details."}</p>
        </div>
      )}
    </div>
  );
}
